import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Flex, Icon, Modal, Typo, theme } from '../../../ui';
import { useClassroomEvents } from '../hooks';
import { CALENDAR_EVENT_COLORS } from '../data/classroomConstants';
import AddEventModal from './AddEventModal';

const CalendarWrapper = styled.div`
  padding: 24px;
  height: 100%;

  .fc {
    height: 100%;
    font-family: inherit;
  }

  .fc .fc-toolbar-title {
    font-size: 18px;
    font-weight: 600;
  }

  .fc .fc-button {
    background: ${theme.colors.PRIMARY};
    border-color: ${theme.colors.PRIMARY};
    font-size: 13px;
    padding: 6px 12px;
    text-transform: none;

    &:hover {
      background: ${theme.colors.PRIMARY};
      opacity: 0.9;
    }

    &:disabled {
      background: ${theme.colors.NEU_200};
      border-color: ${theme.colors.NEU_200};
    }
  }

  .fc .fc-button-active {
    background: ${theme.colors.PRIMARY};
    border-color: ${theme.colors.PRIMARY};
    opacity: 0.85;
  }

  .fc .fc-event {
    border-radius: 4px;
    padding: 2px 4px;
    font-size: 12px;
    cursor: pointer;
  }

  .fc .fc-daygrid-event {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .fc .fc-day-today {
    background: ${theme.colors.PRIMARY_EXTRA_LIGHT} !important;
  }

  .fc .fc-col-header-cell {
    padding: 8px 0;
    font-size: 13px;
    font-weight: 500;
    color: ${theme.colors.NEU_600};
  }

  .fc .fc-timegrid-slot {
    height: 48px;
  }
`;

const TopBar = styled(Flex)`
  padding: 0 24px 8px;
`;

const LegendItem = styled(Flex)`
  font-size: 12px;
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const AddEventButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: transparent;
  color: ${theme.colors.PRIMARY};
  border: 1px solid ${theme.colors.PRIMARY};
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background 0.15s;

  &:hover {
    background: ${theme.colors.PRIMARY_EXTRA_LIGHT};
  }
`;

const EventDetailRow = styled(Flex)`
  padding: 8px 0;
  border-bottom: 1px solid ${theme.colors.NEU_100};

  &:last-child {
    border-bottom: none;
  }
`;

const ClassroomCalendar = ({ groupId }) => {
  const { t } = useTranslation();
  const { events, isLoading } = useClassroomEvents(groupId);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [localEvents, setLocalEvents] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [prefilledDate, setPrefilledDate] = useState('');

  const calendarEvents = useMemo(() => [...(events || []), ...localEvents], [events, localEvents]);

  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;
    setSelectedEvent({
      title: event.title,
      start: event.start,
      end: event.end,
      allDay: event.allDay,
      type: event.extendedProps?.type,
      sectionName: event.extendedProps?.sectionName,
      teacherName: event.extendedProps?.teacherName,
    });
  };

  const handleDateClick = (info) => {
    setPrefilledDate(info.dateStr?.split('T')[0] || info.dateStr);
    setShowCreateModal(true);
  };

  const handleAddEvent = (newEvent) => {
    setLocalEvents((prev) => [...prev, newEvent]);
  };

  const formatTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString([], {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  const getEventTypeLabel = (type) => {
    switch (type) {
      case 'live_session':
        return t('classroom.liveSession', 'Live Session');
      case 'assignment_due':
        return t('classroom.assignmentDue', 'Assignment Due');
      case 'exam':
        return t('classroom.exam', 'Exam');
      case 'custom':
        return t('classroom.custom', 'Custom');
      default:
        return t('classroom.event', 'Event');
    }
  };

  if (isLoading) return null;

  return (
    <>
      <TopBar justify='space-between' align='center'>
        <Flex gap='16' align='center'>
          <LegendItem gap='6' align='center'>
            <LegendDot $color={CALENDAR_EVENT_COLORS.live_session} />
            <Typo.SmallText color={theme.colors.NEU_600}>
              {t('classroom.liveSession', 'Live Session')}
            </Typo.SmallText>
          </LegendItem>
          <LegendItem gap='6' align='center'>
            <LegendDot $color={CALENDAR_EVENT_COLORS.assignment_due} />
            <Typo.SmallText color={theme.colors.NEU_600}>
              {t('classroom.assignmentDue', 'Assignment Due')}
            </Typo.SmallText>
          </LegendItem>
          <LegendItem gap='6' align='center'>
            <LegendDot $color={CALENDAR_EVENT_COLORS.exam} />
            <Typo.SmallText color={theme.colors.NEU_600}>
              {t('classroom.exam', 'Exam')}
            </Typo.SmallText>
          </LegendItem>
          <LegendItem gap='6' align='center'>
            <LegendDot $color={CALENDAR_EVENT_COLORS.custom} />
            <Typo.SmallText color={theme.colors.NEU_600}>
              {t('classroom.custom', 'Custom')}
            </Typo.SmallText>
          </LegendItem>
        </Flex>

        <AddEventButton onClick={() => { setPrefilledDate(''); setShowCreateModal(true); }}>
          <Icon.Add size='x-small' color={theme.colors.PRIMARY} />
          {t('classroom.addEvent', 'Add Event')}
        </AddEventButton>
      </TopBar>

      <CalendarWrapper>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView='timeGridWeek'
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek',
          }}
          events={calendarEvents}
          eventClick={handleEventClick}
          dateClick={handleDateClick}
          nowIndicator
          allDaySlot
          slotMinTime='07:00:00'
          slotMaxTime='20:00:00'
          weekends={false}
          height='100%'
          eventDisplay='block'
          dayMaxEvents={3}
          buttonText={{
            today: t('classroom.calendarToday', 'Today'),
            month: t('classroom.calendarMonth', 'Month'),
            week: t('classroom.calendarWeek', 'Week'),
          }}
        />
      </CalendarWrapper>

      {selectedEvent && (
        <Modal
          open
          onClose={() => setSelectedEvent(null)}
          title={selectedEvent.title}
          width='440px'
        >
          <Flex direction='column' gap='4'>
            <EventDetailRow gap='10' align='center'>
              <Icon.Calendar color={theme.colors.NEU_300} size='x-small' />
              <Flex direction='column' gap='2'>
                <Typo.SmallText color={theme.colors.NEU_300}>
                  {t('classroom.date', 'Date')}
                </Typo.SmallText>
                <Typo.MediumText>{formatDate(selectedEvent.start)}</Typo.MediumText>
              </Flex>
            </EventDetailRow>

            {!selectedEvent.allDay && (
              <EventDetailRow gap='10' align='center'>
                <Icon.Time color={theme.colors.NEU_300} size='x-small' />
                <Flex direction='column' gap='2'>
                  <Typo.SmallText color={theme.colors.NEU_300}>
                    {t('classroom.time', 'Time')}
                  </Typo.SmallText>
                  <Typo.MediumText>
                    {formatTime(selectedEvent.start)} — {formatTime(selectedEvent.end)}
                  </Typo.MediumText>
                </Flex>
              </EventDetailRow>
            )}

            <EventDetailRow gap='10' align='center'>
              <Icon.Course color={theme.colors.NEU_300} size='x-small' />
              <Flex direction='column' gap='2'>
                <Typo.SmallText color={theme.colors.NEU_300}>
                  {t('classroom.unit', 'Unit')}
                </Typo.SmallText>
                <Typo.MediumText>{selectedEvent.sectionName}</Typo.MediumText>
              </Flex>
            </EventDetailRow>

            {selectedEvent.teacherName && (
              <EventDetailRow gap='10' align='center'>
                <Icon.Profile color={theme.colors.NEU_300} size='x-small' />
                <Flex direction='column' gap='2'>
                  <Typo.SmallText color={theme.colors.NEU_300}>
                    {t('classroom.teacher', 'Teacher')}
                  </Typo.SmallText>
                  <Typo.MediumText>{selectedEvent.teacherName}</Typo.MediumText>
                </Flex>
              </EventDetailRow>
            )}

            <EventDetailRow gap='10' align='center'>
              <Icon.Info color={theme.colors.NEU_300} size='x-small' />
              <Flex direction='column' gap='2'>
                <Typo.SmallText color={theme.colors.NEU_300}>
                  {t('classroom.type', 'Type')}
                </Typo.SmallText>
                <Typo.MediumText>{getEventTypeLabel(selectedEvent.type)}</Typo.MediumText>
              </Flex>
            </EventDetailRow>

            {selectedEvent.type === 'live_session' && (
              <Flex style={{ marginTop: 16 }}>
                <button
                  style={{
                    padding: '10px 20px',
                    background: theme.colors.PRIMARY,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {t('classroom.joinSession', 'Join Live Session')}
                </button>
              </Flex>
            )}
          </Flex>
        </Modal>
      )}

      {showCreateModal && (
        <AddEventModal
          open
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleAddEvent}
          prefilledDate={prefilledDate}
        />
      )}
    </>
  );
};

export default ClassroomCalendar;
